import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Divider, List, ListItemText, Paper, Button, TextField,
    CircularProgress, Snackbar, ListItem, ListItemButton, Slider, MenuItem, Select, FormControl, InputLabel, Switch
} from '@mui/material';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteIcon from '@mui/icons-material/Delete';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

const API_BASE = 'https://adam.app.autofront.com.au/api';

const SettingsPage = () => {
    const [selectedMenu, setSelectedMenu] = useState('general');
    const [settings, setSettings] = useState([]);
    const [corrections, setCorrections] = useState([]);
    const [selectedPrompt, setSelectedPrompt] = useState(null);
    const [selectedCorrection, setSelectedCorrection] = useState(null);
    const [promptInput, setPromptInput] = useState('');
    const [saving, setSaving] = useState(false);
    const [newCategory, setNewCategory] = useState('');
    const [newPrompt, setNewPrompt] = useState('');
    const [adding, setAdding] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [keywords, setKeywords] = useState({});
    const [newKeyword, setNewKeyword] = useState({});
    const [priorityList, setPriorityList] = useState([]);
    const [botConfig, setBotConfig] = useState({
        maxTokens: 1000,
        temperature: 0.7,
        similarityThreshold: 0.8,
    });
    const [editingCategory, setEditingCategory] = useState(null);
    const [editedCategory, setEditedCategory] = useState('');
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        fetchData();
        fetchPriority();
    }, []);

    const fetchData = async () => {
        try {
            const [settingsRes, correctionsRes, keywordRes, configRes] = await Promise.all([
                fetch(`${API_BASE}/settings`),
                fetch(`${API_BASE}/corrections`),
                fetch(`${API_BASE}/settings/keywords`),
                fetch(`${API_BASE}/settings/config/bot`),
            ]);

            if (!settingsRes.ok || !correctionsRes.ok || !keywordRes.ok || !configRes.ok) {
                throw new Error('Failed to fetch one or more resources');
            }

            const [settingsData, correctionsData, keywordsData, configData] = await Promise.all([
                settingsRes.json(),
                correctionsRes.json(),
                keywordRes.json(),
                configRes.json(),
            ]);

            setSettings(settingsData);
            setCorrections(correctionsData);
            setKeywords(keywordsData);
            setBotConfig(configData);
        } catch (err) {
            console.error('Failed to fetch data:', err);
        }
    };

    const fetchPriority = async () => {
        const res = await fetch(`${API_BASE}/settings/priority`);
        const data = await res.json();
        setPriorityList(data);
    };

    const handleMenuSelect = (menu) => {
        setSelectedMenu(menu);
    };

    const handleSettingChange = (key, value) => {
        setBotConfig((prev) => ({ ...prev, [key]: value }));
    };

    const handlePromptSelect = (category, prompt) => {
        setSelectedCorrection(null);
        setSelectedPrompt({ category, prompt });
        setPromptInput(prompt);
    };

    const handleCorrectionSelect = (correction) => {
        setSelectedPrompt(null);
        const context = correction.context_json ? JSON.parse(correction.context_json) : [];
        const previousMessagesCount = context.length;
        setSelectedCorrection({ ...correction, context, previousMessagesCount });
    };

    const handleKeywordAdd = (category) => {
        if (!newKeyword[category]) return;
        setKeywords((prev) => ({
            ...prev,
            [category]: [...(prev[category] || []), newKeyword[category]],
        }));
        setNewKeyword((prev) => ({ ...prev, [category]: '' }));
    };

    const handleKeywordDelete = (category, keyword) => {
        setKeywords((prev) => ({
            ...prev,
            [category]: prev[category].filter((kw) => kw !== keyword),
        }));
    };

    const handleSaveBotConfig = async () => {
        await fetch(`${API_BASE}/settings/config/bot`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(botConfig),
        });
        setSnackbarMessage('Bot config saved');
    };

    const handleEditCategory = (category, prompt) => {
        setEditingCategory(category);
        setEditedCategory(category);
        setPromptInput(prompt); // Set the current prompt for editing
    };

    const handleSaveCategory = async (oldCategory) => {
        try {
            await fetch(`${API_BASE}/settings/${oldCategory}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ category: editedCategory, prompt: promptInput }),
            });
            fetchData(); // Refresh the categories
            setEditingCategory(null);
        } catch (err) {
            console.error('Failed to save category:', err);
        }
    };

    const handleDeleteCorrection = async (correctionId) => {
        setDeleting(true);
        try {
            const res = await fetch(`${API_BASE}/corrections/${correctionId}`, {
                method: 'DELETE',
            });
            if (res.ok) {
                fetchData(); // Refresh corrections
            }
        } catch (err) {
            console.error('Failed to delete correction:', err);
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Box display="flex" height="100vh">
            {/* Sidebar Menu */}
            <Box width="250px" bgcolor="#f5f5f5" p={2}>
                <Typography variant="h6" gutterBottom>
                    Settings
                </Typography>
                <List>
                    <ListItem button selected={selectedMenu === 'general'} onClick={() => handleMenuSelect('general')}>
                        <ListItemText primary="General" />
                    </ListItem>
                    <ListItem button selected={selectedMenu === 'corrections'} onClick={() => handleMenuSelect('corrections')}>
                        <ListItemText primary="Corrections" />
                    </ListItem>
                    <ListItem button selected={selectedMenu === 'keywords'} onClick={() => handleMenuSelect('keywords')}>
                        <ListItemText primary="Keywords" />
                    </ListItem>
                    <ListItem button selected={selectedMenu === 'chatgpt'} onClick={() => handleMenuSelect('chatgpt')}>
                        <ListItemText primary="ChatGPT Configuration" />
                    </ListItem>
                </List>
            </Box>

            {/* Main Content */}
            <Box flex={1} p={3}>
                {selectedMenu === 'general' && (
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            General Settings
                        </Typography>
                        <Divider sx={{ my: 2 }} />
                        <Box mb={3}>
                            <Typography variant="subtitle1">Existing Categories</Typography>
                            <List>
                                {settings.map((setting) => (
                                    <ListItem key={setting.category}>
                                        {editingCategory === setting.category ? (
                                            <>
                                                <TextField
                                                    label="Category"
                                                    value={editedCategory}
                                                    onChange={(e) => setEditedCategory(e.target.value)}
                                                    fullWidth
                                                    sx={{ mb: 2 }}
                                                />
                                                <TextField
                                                    label="Prompt"
                                                    value={promptInput}
                                                    onChange={(e) => setPromptInput(e.target.value)}
                                                    multiline
                                                    fullWidth
                                                    minRows={5}
                                                    sx={{ mb: 2 }}
                                                />
                                                <Button
                                                    variant="contained"
                                                    onClick={() => handleSaveCategory(setting.category)}
                                                >
                                                    Save
                                                </Button>
                                            </>
                                        ) : (
                                            <>
                                                <ListItemText
                                                    primary={setting.category}
                                                    secondary={setting.prompt}
                                                />
                                                <Button
                                                    variant="outlined"
                                                    onClick={() => handleEditCategory(setting.category, setting.prompt)}
                                                    sx={{ ml: 2 }}
                                                >
                                                    Edit
                                                </Button>
                                            </>
                                        )}
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                        <Box mb={3}>
                            <Typography variant="subtitle1">Add New Category</Typography>
                            <TextField
                                label="Category"
                                value={newCategory}
                                onChange={(e) => setNewCategory(e.target.value)}
                                fullWidth
                                sx={{ my: 1 }}
                            />
                            <TextField
                                label="Prompt"
                                value={newPrompt}
                                onChange={(e) => setNewPrompt(e.target.value)}
                                multiline
                                fullWidth
                                minRows={3}
                                sx={{ mb: 1 }}
                            />
                            <Button
                                variant="contained"
                                onClick={async () => {
                                    setAdding(true);
                                    try {
                                        const res = await fetch(`${API_BASE}/settings/${newCategory}`, {
                                            method: 'PUT',
                                            headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify({ prompt: newPrompt }),
                                        });
                                        if (res.ok) {
                                            fetchData(); // Refresh the categories
                                            setNewCategory('');
                                            setNewPrompt('');
                                        }
                                    } catch (err) {
                                        console.error('Failed to add category:', err);
                                    } finally {
                                        setAdding(false);
                                    }
                                }}
                                disabled={adding}
                            >
                                {adding ? <CircularProgress size={20} /> : 'Add Category'}
                            </Button>
                        </Box>
                    </Paper>
                )}

                {selectedMenu === 'corrections' && (
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Corrections
                        </Typography>
                        <Divider sx={{ my: 2 }} />
                        <List>
                            {corrections.map((correction) => (
                                <ListItem
                                    key={correction.id}
                                    button
                                    onClick={() => handleCorrectionSelect(correction)}
                                >
                                    <ListItemText
                                        primary={`Correction #${correction.id}`}
                                        secondary={new Date(correction.timestamp).toLocaleString()}
                                    />
                                    <IconButton
                                        edge="end"
                                        onClick={(e) => {
                                            e.stopPropagation(); // Prevent triggering correction details
                                            handleDeleteCorrection(correction.id);
                                        }}
                                        disabled={deleting}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </ListItem>
                            ))}
                        </List>
                        {selectedCorrection && (
                            <Box mt={3}>
                                <Typography variant="subtitle1">Correction Details</Typography>
                                <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                                    <Typography variant="body2">
                                        <strong>Category:</strong> {selectedCorrection.category}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Original Response:</strong> {selectedCorrection.original_response}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Corrected Response:</strong> {selectedCorrection.corrected_response}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Context:</strong>
                                    </Typography>
                                    <List dense>
                                        {selectedCorrection.context.map((msg, index) => (
                                            <ListItem key={index}>
                                                <ListItemText
                                                    primary={`${msg.sender}: ${msg.message}`}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Paper>
                            </Box>
                        )}
                    </Paper>
                )}

                {selectedMenu === 'keywords' && (
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Keywords
                        </Typography>
                        <Divider sx={{ my: 2 }} />
                        {Object.keys(keywords).map((category) => (
                            <Accordion key={category}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography>{category}</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <List dense>
                                        {(keywords[category] || []).map((kw, i) => (
                                            <ListItem
                                                key={i}
                                                secondaryAction={
                                                    <IconButton edge="end" onClick={() => handleKeywordDelete(category, kw)}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                }
                                            >
                                                <ListItemText primary={kw} />
                                            </ListItem>
                                        ))}
                                    </List>
                                    <Box display="flex" gap={1} mt={1}>
                                        <TextField
                                            size="small"
                                            label="New keyword"
                                            value={newKeyword[category] || ''}
                                            onChange={(e) =>
                                                setNewKeyword((prev) => ({ ...prev, [category]: e.target.value }))
                                            }
                                        />
                                        <Button onClick={() => handleKeywordAdd(category)} variant="outlined">
                                            Add
                                        </Button>
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Paper>
                )}

                {selectedMenu === 'chatgpt' && (
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            AI behavior
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                             Adjust the Settings for Adam responses. Settings control the lenght, diversity and how closesly Adam should match corrections.
                        </Typography>

                        
                        <Divider sx={{ my: 2 }} />
                        <Box mb={3}>
                            <Typography gutterBottom>Max Tokens</Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                            Max tokens sets the limit on how long an AI’s response can be, with each token representing roughly a word or part of a word.
                            </Typography>
                            <Slider
                                value={botConfig.maxTokens}
                                onChange={(e, value) => handleSettingChange('maxTokens', value)}
                                min={100}
                                max={1000}
                                step={100}
                                marks={[
                                    { value: 100, label: '100' },
                                    { value: 200, label: '200' },
                                    { value: 300, label: '300' },
                                    { value: 400, label: '400' },
                                    { value: 500, label: '500' },
                                    { value: 600, label: '600' },
                                    { value: 700, label: '700' },
                                    { value: 800, label: '800' },
                                    { value: 900, label: '900' },
                                    { value: 1000, label: '1000' },
                                ]}
                                valueLabelDisplay="auto"
                            />
                        </Box>
                        <Box mb={3}>
                            <Typography gutterBottom>Temperature</Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                            AI temperature controls the randomness of an AI model’s responses: lower values make outputs more focused and predictable, while higher values make them more diverse and creative.
                            </Typography>
                            <Slider
                                value={botConfig.temperature}
                                onChange={(e, value) => handleSettingChange('temperature', value)}
                                min={0}
                                max={1}
                                step={0.1}
                                marks={[
                                    { value: 0.1, label: '0.1' },
                                    { value: 0.2, label: '0.2' },
                                    { value: 0.3, label: '0.3' },
                                    { value: 0.4, label: '0.4' },
                                    { value: 0.5, label: '0.5' },
                                    { value: 0.6, label: '0.6' },
                                    { value: 0.7, label: '0.7' },
                                    { value: 0.8, label: '0.8' },
                                    { value: 0.9, label: '0.9' },
                                    { value: 1, label: '1' },

                                ]}
                                valueLabelDisplay="auto"
                            />
                        </Box>
                        <Box mb={3}>
                            <Typography gutterBottom>Similarity Threshold</Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                            Adjust the similarity threshold to control how closely corrections are evaluated and used. Higher values mean stricter matching.
                            </Typography>
                            <Slider
                                value={botConfig.similarityThreshold}
                                onChange={(e, value) => handleSettingChange('similarityThreshold', value)}
                                min={10}
                                max={100}
                                step={0.1}
                                marks={[
                                    { value: 10, label: '10%' },
                                    { value: 20, label: '20%' },
                                    { value: 30, label: '30%' },
                                    { value: 40, label: '40%' },
                                    { value: 50, label: '50%' },
                                    { value: 60, label: '60%' },
                                    { value: 70, label: '70%' },
                                    { value: 80, label: '80%' },
                                    { value: 90, label: '90%' },
                                    { value: 100, label: '100%' },

                                ]}
                                valueLabelDisplay="auto"
                            />
                        </Box>
                        <Button variant="contained" onClick={handleSaveBotConfig}>
                            Save Configuration
                        </Button>
                    </Paper>
                )}
            </Box>

            <Snackbar
                open={!!snackbarMessage}
                autoHideDuration={3000}
                onClose={() => setSnackbarMessage('')}
                message={snackbarMessage}
            />
        </Box>
    );
};

export default SettingsPage;